import { useMemo } from "react"

import { useNeoletterUserProfile } from "shared/api/neoletter-user-profile/useNeoletterUserProfile"
import { NEW_PROJECT_OVERLAY } from "shared/context/overlay/types"
import { useEvent } from "shared/lib/useEvent"
import { useOverlay } from "shared/lib/useOverlay"
import { Project } from "shared/types/Project"
import { PartialWithID } from "shared/types/WithID"

import { useNavigateToProject } from "entities/projects/model/useNavigateToProject"
import { useProjectDetailsHref } from "entities/projects/model/useProjectDetailsHref"
import { useProjectOptions } from "entities/projects/model/useProjectOptions"

import { ActiveProjectSelectModel } from "../ActiveProjectSelect"

export function useActiveProjectSelectModel(): ActiveProjectSelectModel {
  const { activeProjectID, updateProfile } = useNeoletterUserProfile()
  const navigateToActiveProject = useNavigateToProject(activeProjectID)
  const navigateToProject = useNavigateToProject(null)
  const activeProjectHref = useProjectDetailsHref(activeProjectID ?? "")
  const { openOverlay } = useOverlay(NEW_PROJECT_OVERLAY)
  const { projects, options, isLoading } = useProjectOptions()

  const activeProject = useMemo(
    () => projects?.find(project => project.id === activeProjectID) || null,
    [projects, activeProjectID]
  )

  const handleChange = useEvent(async (projectID: string | null) => {
    if (projectID) {
      updateProfile({ customFields: { active_project_id: projectID } })
      navigateToProject({ id: projectID })
    }
  })

  const handleCreate = useEvent((project: PartialWithID<Project>) => {
    openOverlay({
      defaultValue: project,
      navigateAfterSubmit: true
    })
  })

  return {
    activeProject,
    activeProjectHref,
    value: activeProjectID,
    options,
    isLoading,
    onNavigateToActiveProject: navigateToActiveProject,
    onChange: handleChange,
    onCreate: handleCreate
  }
}
